export type CommunicationChannel = 'call' | 'text' | 'booking';
export type BookingStatus = 'needs_booking' | 'booked' | 'attached' | 'follow_up' | 'completed' | 'cancelled' | 'unbooked';
export type CommunicationMessage = {
    id: string;
    direction: 'inbound' | 'outbound';
    body: string;
    createdAt: string;
    media?: Array<{ id?: string; url?: string | null; contentType: string | null; width?: number | null; height?: number | null; status?: 'pending' | 'available' | 'missing' | 'unsupported' | 'failed'; contentUrl?: string | null }>;
    senderName?: string | null;
    providerStatus?: string | null;
    providerErrorCode?: string | null;
};
export type Communication = {
    outcome?: ActiveCall['outcome'];
    answerStatus?: 'answered' | 'unanswered';
    answeredByName?: string | null;
    assignedToName?: string | null;
    status?: string;
    blocked?: boolean | number;
    id: string;
    channel: CommunicationChannel;
    customerName: string | null;
    customerId: string | null;
    phone: string | null;
    summary: string | null;
    bookingStatus: BookingStatus;
    activityAt: string;
    scheduledStart: string | null;
    jobId: string | null;
    jobNumber: string | null;
    sourceLabel: string;
    messages?: CommunicationMessage[];
    reviewedAt?: string | null;
    messageCount?: number;
    recordingUrl?: string | null;
    recordingStatus?: "available" | "unavailable" | "not_recorded" | "pending";
    recordingDurationSeconds?: number | null;
    transcriptStatus?: 'pending' | 'processing' | 'available' | 'failed' | null;
    transcriptText?: string | null;
    transcriptCompletedAt?: string | null;
    unread?: number;
    providerStatus?: string | null;
    messaging?: { code: 'ready' | 'disabled' | 'not_configured' | 'sender_mismatch'; available: boolean; message: string };
    textingStatus?: 'opted_out' | 'opted_in' | 'unknown';
    canReply?: boolean;
};
export type ActiveCall = {
    supportsFollowUp?: boolean;
    isLive?: boolean;
    locationId?: string | null;
    locationAddress?: string | null;
    direction?: 'inbound' | 'outbound';
    customerId?: string | null;
    outcome?: 'booked' | 'unbooked' | 'attached' | 'cancelled' | 'follow_up' | null;
    classification?: 'service_not_booked' | 'employment' | 'directions' | 'technician' | 'general' | 'other' | null;
    answerStatus?: 'answered' | 'unanswered';
    answeredByName?: string | null;
    answeredAt?: string | null;
    resolvedByName?: string | null;
    resolvedAt?: string | null;
    jobId?: string | null;
    jobNumber?: string | null;
    jobLinkStatus?: 'linked' | 'unrecognized' | 'no_active_job' | 'multiple_active_jobs' | 'one_active_job';
    notes?: string | null;
    durationSeconds?: number | null;
    endedAt?: string | null;
    recordingUrl?: string | null;
    recordingStatus?: Communication["recordingStatus"];

    status?: string;
    assignedToUserId?: string | null;
    assignedToName?: string | null;
    claimedAt?: string | null;
    isMine?: boolean;
    id: string;
    customerName: string | null;
    phone: string;
    state: 'ringing' | 'active';
    startedAt: string;
};
export type TextThread = {
    customerId?: string | null;
    blocked?: boolean | number;
    status?: string;
    lastDirection?: string | null;
    id: string;
    customerName: string | null;
    phone: string;
    unread: number;
    messages: CommunicationMessage[];
};
export type PhoneData = {
    /** Total unread incoming texts, including conversations on later pages. */
    unread?: number;
    hasMore?: boolean;
    companyPhone: string | null;
    connected: boolean;
    calls: ActiveCall[];
    threads: TextThread[];
    messaging?: Communication["messaging"];
};
export const bookingLabels: Record<BookingStatus, string> = { needs_booking: 'Needs booking', booked: 'Booked', attached: 'Attached to job', follow_up: 'Follow up', completed: 'Completed', cancelled: 'Cancelled', unbooked: 'Not booked' };
export function searchThreads(threads: TextThread[], query: string) {
    const text = query.trim().toLowerCase();
    const digits = text.replace(/\D/g, '');
    return threads.filter(thread => !text || `${thread.customerName ?? ''} ${thread.phone} ${thread.messages.map(message => message.body).join(' ')}`.toLowerCase().includes(text) || (digits.length >= 3 && thread.phone.replace(/\D/g, '').includes(digits)));
}

export type BookingCaller = { phone: string; customerName: string | null; callId: string; bookingAgentLeadId?: string; bookingNotes?: string | null };
export const disconnectedPhoneData: PhoneData = { companyPhone: null, connected: false, calls: [], threads: [] };
export function matchingCaller<T extends { primaryPhone?: string | null }>(customers: T[], phone: string): T | null {
  const normalize = (value: string) => { const digits = value.replace(/\D/g, ''); return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits; };
  const digits = normalize(phone);
  if (digits.length < 7) return null;
  const matches = customers.filter(customer => normalize(customer.primaryPhone ?? '') === digits);
  return matches.length === 1 ? matches[0] : null;
}

export { formatPhoneNumber as formatCommunicationPhone } from "../lib/phone-format";

export function isLivePhoneCall(call: ActiveCall) {
 if (call.isLive !== undefined) return call.isLive;
 return call.status ? ['ringing', 'in-progress'].includes(call.status) : ['ringing', 'active'].includes(call.state);
}
